// Port of windows/src/SatoriChat.Core/Logic/HistoryRange.cs (Windows 1.0.4, the «solo veo mensajes a
// partir del 9 de septiembre» report, 2026-10-13). Change a rule there → change it here.
//
// The stretch of a conversation the device holds WITHOUT holes.
//
// The store also holds posts that arrived OUT OF SEQUENCE — saved messages, followed-thread roots,
// mentions, search hits, permalinks, a jump's surroundings. A timeline that reads "the newest N roots"
// draws such a post right above the newest page as if nothing lay between them, and paging older
// history from the oldest VISIBLE root then jumps straight over the hole: measured on a real account,
// the reader's biggest DM (13,579 posts) had six saved messages from 2024-08 … 2025-04, so everything
// between the newest page and August 2024 could never be reached.
//
// The range is what the newest page and the older pages chained from it PROVE: [floor, top], every
// post in between is stored. The timeline shows roots at or after the floor (and everything newer than
// top: own sends, socket arrivals), older history is fetched before the FLOOR's post (the server page's
// cursor, never a visible row), and a newest page that does not reach back into the range (offline for
// a while) starts a new range — the walk then re-joins the old one page by page.

export interface HistoryRange {
  /** create_at of the oldest post of the hole-free stretch. */
  floorAt: number;
  /** That post's id: the cursor for the next older page. */
  floorId: string;
  /** create_at of the newest post the stretch was proven to. */
  topAt: number;
  /** The stretch reaches the first message of the conversation: nothing hidden, nothing to fetch. */
  atStart: boolean;
}

/** One post of a server page, as the range needs it. */
export interface PagePost {
  id: string;
  createAt: number;
}

export const HistoryRanges = {
  /** Roots older than this are out-of-sequence copies and stay off the timeline; undefined = show everything. */
  notBefore(range: HistoryRange | null | undefined): number | undefined {
    return range && !range.atStart ? range.floorAt : undefined;
  },

  encode(r: HistoryRange): string {
    return `${r.floorAt}|${r.floorId}|${r.topAt}|${r.atStart ? 1 : 0}`;
  },

  decode(raw: string | null | undefined): HistoryRange | null {
    if (!raw) return null;
    const parts = raw.split('|');
    if (parts.length !== 4) return null;
    const floorAt = Number(parts[0]);
    const topAt = Number(parts[2]);
    if (parts[0] === '' || parts[2] === '' || !Number.isInteger(floorAt) || !Number.isInteger(topAt)) return null;
    return { floorAt, floorId: parts[1], topAt, atStart: parts[3] === '1' };
  },

  /**
   * The NEWEST page landed. It extends the range when it reaches back into it (its oldest post is not
   * newer than the range's top); otherwise it is a new range on its own — what lay between is unproven.
   */
  afterNewestPage(current: HistoryRange | null, page: PagePost[], hasOlder: boolean): HistoryRange | null {
    if (!page.length) return hasOlder ? current : { floorAt: 0, floorId: '', topAt: current?.topAt ?? 0, atStart: true };
    const oldest = HistoryRanges.oldest(page);
    const newest = Math.max(...page.map((p) => p.createAt));
    if (current && oldest.createAt <= current.topAt)
      return { ...current, topAt: Math.max(current.topAt, newest), atStart: current.atStart || !hasOlder };
    return { floorAt: oldest.createAt, floorId: oldest.id, topAt: newest, atStart: !hasOlder };
  },

  /**
   * An OLDER page landed. Only a page asked for before the floor's own post extends the range (a
   * jump's anchored window pages from elsewhere and proves nothing about the stretch).
   */
  afterOlderPage(current: HistoryRange | null, beforePostId: string, page: PagePost[], hasMore: boolean): HistoryRange | null {
    if (!current || current.atStart || current.floorId !== beforePostId) return current;
    if (!page.length) return { ...current, atStart: true };
    const oldest = HistoryRanges.oldest(page);
    return { ...current, floorAt: Math.min(current.floorAt, oldest.createAt), floorId: oldest.id, atStart: !hasMore };
  },

  /** The page's oldest post — ties broken by id, as the server orders them. */
  oldest(page: PagePost[]): PagePost {
    return page.reduce((a, b) => (a.createAt === b.createAt ? (a.id < b.id ? a : b) : a.createAt < b.createAt ? a : b));
  },

  same(a: HistoryRange | null, b: HistoryRange | null): boolean {
    if (!a || !b) return a === b;
    return a.floorAt === b.floorAt && a.floorId === b.floorId && a.topAt === b.topAt && a.atStart === b.atStart;
  },
};

// FILL (hotfix 2026-10-14, Mac 1.0.325 «Cotizaciones/Aprobaciones de ingreso» showed "No messages yet"):
// the stretch shows roots at/after the floor, and in a channel where bots answer inside threads the
// newest page can be ALL replies — no root after the floor, an empty timeline that never scrolls, so
// nothing asked for more. Same rule on every client.

/** The timeline keeps fetching older pages by itself while it shows fewer roots than this. */
export const FILL_MIN_ROOTS = 15;
/** Automatic pages per fill; reset when the conversation opens or the reader asks by hand. */
export const FILL_MAX_PAGES = 40;

export const HistoryFill = {
  /**
   * Fetch the next older page from the floor WITHOUT a scroll? Independent of the barren-ask cap:
   * a fill has its own (`FILL_MAX_PAGES`).
   */
  shouldFetch(s: { roots: number; reachedStart: boolean; lastFailed: boolean; pagesThisFill: number }): boolean {
    return s.roots < FILL_MIN_ROOTS && !s.reachedStart && !s.lastFailed && s.pagesThisFill < FILL_MAX_PAGES;
  },

  /** "No messages yet" only when the stretch holds no root AND reaches the first message. */
  showsEmptyState(roots: number, reachedStart: boolean): boolean {
    return roots === 0 && reachedStart;
  },
};
